/*
 * QuoteVersionForm.tsx : Un formulaire pour ajouter ou modifier une version sur un devis.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faBuilding,
	faCalendarAlt,
	faFileContract,
	faInfoCircle,
	faUserTie,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	InputField,
	DatePickerField,
	TextAreaField,
	SelectField,
	ToggleButtonField,
	finishFormSave,
	FormSaveActions,
	formatLocalDate,
	parseLocalDate,
	useFormDirty,
} from '../../../common';
import CardHeader from 'layout/CardHeader';
import CardSection from 'layout/CardSection';

// Import Devis
import { QuoteVersionData, signatoryOptions } from '../types/types';
import { quotesVersionApi } from '../services/quoteVersionApi';

// Import Contacts
import { contactsApi } from 'features/contacts/services/contactsApi';

// Import User
import { usersApi } from 'features/users/services/usersApi';

// Import des tiers
import { tiersApi } from 'features/tiers/services/tiersApi';

// Composant QuoteVersionForm
const QuoteVersionForm: React.FC = () => {
	// Hook de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook de Formulaire
	const [formData, setFormData] = React.useState<QuoteVersionData>({
		id: 0,
		label: '',
		status: 'DRAFT',
		versionIndex: 0,
		quote: { id: 0, referenceNumber: '', budgeted: false, quoteVersions: [] },
		quoteLines: [],
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Hook pour les options de sélection des contacts
	const [contactOptions, setContactOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingContacts, setLoadingContacts] = React.useState(true);

	// Hook pour les options de sélection des utilisateurs
	const [userOptions, setUserOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingUsers, setLoadingUsers] = React.useState(true);

	// Hook pour les options de sélection des tiers liés
	const [relatedTiersOptions, setRelatedTiersOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingRelatedTiers, setLoadingRelatedTiers] = React.useState(true);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si un id est présent, on fetch la version du devis
		if (id) {
			quotesVersionApi
				.get(Number(id), token)
				.then((data: QuoteVersionData) => {
					setFormData(data);
					// Préremplir les selects
					if (data.businessManager) {
						setContactOptions([
							{
								key: data.businessManager.id.toString(),
								label:
									data.businessManager.firstName +
									' ' +
									data.businessManager.lastName,
							},
						]);
					}
					if (data.assignedUser) {
						setUserOptions([
							{
								key: data.assignedUser.id.toString(),
								label:
									data.assignedUser.firstName + ' ' + data.assignedUser.name,
							},
						]);
					}
				})
				.catch((error) => {
					console.error('Erreur lors du chargement du devis :', error);
					setLoading(false);
				})
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Lazy loading : options du select Contacts
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingContacts) {
			contactsApi
				.listPaginated(token, { limit: 999999 })
				.then((res) => {
					// on filtre data pour ne garder que les contacts dont tiers.id est égal à formData.quote.tiers.id et dont le champs businessOrder est égal à true
					const filteredContacts = res.data.filter(
						(contact) =>
							contact.tiers?.id === formData.quote.tiers?.id &&
							contact.businessManager === true
					);
					setContactOptions(
						filteredContacts.map((contact) => ({
							key: contact.id.toString(),
							label: contact.firstName + ' ' + contact.lastName,
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingContacts(false));
		}
	}, [loading, loadingContacts]);

	// Lazy loading : options du select Users
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingUsers) {
			usersApi
				.listSelect(token)
				.then((items) => {
					setUserOptions(
						items
							.filter((item) => item.id != null)
							.map((item) => ({
								key: String(item.id),
								label: String(item.label),
							}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingUsers(false));
		}
	}, [loading, loadingUsers]);

	// Lazy loading : options du select Related Tiers
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingRelatedTiers) {
			tiersApi
				.listPaginated(token, {
					sort: 'label',
					order: 'ASC',
					limit: 999999,
				})
				.then((res) => {
					setRelatedTiersOptions(
						res.data
							.filter((tiers) => tiers.id != null)
							.map((tiers) => ({
								key: String(tiers.id),
								label: tiers.label ?? '',
							}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingRelatedTiers(false));
		}
	}, [loading, loadingRelatedTiers]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		// Si un id est présent, mise à jour du devis, puis on navigue vers la page de la dernière version du devis
		if (id) {
			quotesVersionApi
				.update(Number(id), formData, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate(`/app/quoteVersions/${formData.id}/manage`),
						markClean,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la mise à jour du devis :', error)
				)
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'une nouvelle version de devis
			quotesVersionApi
				.create(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave: () => navigate('/app/quotes'),
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/quoteVersions/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) =>
					console.error('Erreur lors de la création du devis :', error)
				)
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// Handle Formulaire : Changements des inputs et textareas
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'businessManager') {
			const selectedOption = contactOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					businessManager: {
						id: Number(selectedOption.key),
						lastName: selectedOption.label.split(' ').slice(-1)[0],
						firstName: selectedOption.label.split(' ').slice(0, -1).join(' '),
					},
				}));
			}
			return;
		}

		if (name === 'assignedUser') {
			const selectedOption = userOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					assignedUser: {
						id: Number(selectedOption.key),
						name: selectedOption.label.split(' ').slice(-1)[0],
						firstName: selectedOption.label.split(' ').slice(0, -1).join(' '),
					},
				}));
			}
			return;
		}

		if (name === 'signatory') {
			// On utilise les options de signatoryOptions définies dans le type
			const selectedOption = signatoryOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					signatory: selectedOption.label,
				}));
			}
			return;
		}

		if (name === 'relatedTiers') {
			const selectedOption = relatedTiersOptions.find(
				(opt) => opt.key === value
			);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					relatedTiers: {
						id: Number(selectedOption.key),
						label: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Rendu du composant principal
	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<CardHeader
					icon={faFileContract}
					title={
						id
							? "Édition d'une version de devis" +
								(formData.quote.referenceNumber
									? ' (' +
										formData.quote.referenceNumber +
										(formData.versionIndex.toString()
											? ' - V' + formData.versionIndex.toString()
											: '') +
										')'
									: '')
							: 'Nouvelle version de devis'
					}
					createdAt={
						formData.createdAt
							? formData.createdAt?.toLocaleString()
							: undefined
					}
					createdBy={
						formData.createdBy
							? formData.createdBy.firstName + ' ' + formData.createdBy.name
							: undefined
					}
					loading={loading}
				/>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-8">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
							{/* COLONNE GAUCHE */}
							<CardSection icon={faInfoCircle} title="Informations générales">
								<InputField
									label="Statut"
									name="status"
									value={formData.status}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : DRAFT"
									readOnly={true}
									visibility={false}
								/>
								<InputField
									label="Libellé de version"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : Première version du devis M25001Q"
								/>
								<SelectField
									label="Pilote (interne)"
									selectedKey={formData.assignedUser?.id?.toString() || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'assignedUser',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={userOptions}
									loading={loading}
									optionsLoading={loadingUsers}
									entityHref={
										formData.assignedUser?.id
											? `/app/users/${formData.assignedUser.id}/edit`
											: undefined
									}
									onClear={() =>
										setFormData((prev) => ({
											...prev,
											assignedUser: null,
										}))
									}
								/>
								<SelectField
									label="Pré-sélection du Signataire"
									selectedKey={formData.signatory || ''}
									onValueChange={(val) =>
										handleChangeSelect({
											target: {
												name: 'signatory',
												value: val,
											},
										} as React.ChangeEvent<HTMLSelectElement>)
									}
									options={signatoryOptions}
									loading={false}
									optionsLoading={false}
								/>
								<InputField
									label="Signataire (interne)"
									name="signatory"
									value={formData.signatory}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : M. Le Directeur"
								/>
								<InputField
									label="Motif de validation sans commande"
									name="validationWithoutOrderReason"
									value={formData.validationWithoutOrderReason ?? ''}
									onChange={handleChange}
									loading={loading}
									placeholder="Bon pour accord reçu par mail, …"
								/>
								<TextAreaField
									label="Texte type"
									name="generatedText"
									value={formData.generatedText}
									onChange={handleChange}
									required
									loading={loading}
									placeholder=""
								/>
							</CardSection>

							{/* COLONNE CENTRE */}
							<CardSection icon={faCalendarAlt} title="Dates">
								<DatePickerField
									label="Date d'édition"
									name="editedAt"
									value={parseLocalDate(formData.editedAt)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, editedAt: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date de début de validité"
									name="startDate"
									value={parseLocalDate(formData.startDate)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, startDate: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date de fin de validité"
									name="endDate"
									value={parseLocalDate(formData.endDate)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, endDate: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date de début des activités"
									name="activityStartDate"
									value={parseLocalDate(formData.activityStartDate)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, activityStartDate: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<DatePickerField
									label="Date de fin des activités"
									name="activityEndDate"
									value={parseLocalDate(formData.activityEndDate)}
									onChange={(date) => {
										const isoDate = date ? formatLocalDate(date) : '';
										setFormData({ ...formData, activityEndDate: isoDate });
									}}
									placeholder="Sélectionnez une date"
									loading={loading}
								/>
								<InputField
									label="Exercice"
									name="fiscalYear"
									value={formData.fiscalYear}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : 2024"
								/>
							</CardSection>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
							<CardSection
								icon={faFileContract}
								title="Mentions supplémentaires sur le devis"
							>
								<InputField
									label="Objet du devis"
									name="commentIndex"
									value={formData.commentIndex}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="ex : Lancage 73/19"
								/>
								<TextAreaField
									label="Environnement et maquettes utilisées"
									name="contextNotes"
									value={formData.contextNotes}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="Environnement chantiers AK OFC"
								/>
								<TextAreaField
									label="Observations"
									name="publicNotes"
									value={formData.publicNotes}
									onChange={handleChange}
									required
									loading={loading}
									placeholder="- 21 personnes sur 11 jours
                                                 - poste en 3x8"
								/>
							</CardSection>

							<div className="space-y-8">
								<CardSection icon={faUserTie} title="Client">
									<InputField
										label="Votre référence "
										name="tiersReference"
										value={formData.tiersReference}
										onChange={handleChange}
										required
										loading={loading}
										placeholder="ex : DEVIS-2025-001"
									/>
									<SelectField
										label="Chargé d'affaire (client)"
										selectedKey={formData.businessManager?.id?.toString() || ''}
										onValueChange={(val) =>
											handleChangeSelect({
												target: {
													name: 'businessManager',
													value: val,
												},
											} as React.ChangeEvent<HTMLSelectElement>)
										}
										options={contactOptions}
										loading={loading}
										optionsLoading={loadingContacts}
										entityHref={
											formData.businessManager?.id
												? `/app/contacts/${formData.businessManager.id}/edit`
												: undefined
										}
										onClear={() =>
											setFormData((prev) => ({
												...prev,
												businessManager: null,
											}))
										}
									/>
								</CardSection>

								{/* si le devis est de classification D, K, Q ou T, on affiche le tiers lié et son approbation */}
								{formData.quote.classification &&
								['D', 'K', 'Q', 'T'].includes(
									formData.quote.classification.code
								) ? (
									<CardSection icon={faBuilding} title="Propriétaire maquette">
										{/* Tiers lié */}
										<SelectField
											label="Nom du propriétaire"
											selectedKey={
												formData.relatedTiers
													? String(formData.relatedTiers.id)
													: ''
											}
											onValueChange={(val) =>
												handleChangeSelect({
													target: {
														name: 'relatedTiers',
														value: val,
													},
												} as React.ChangeEvent<HTMLSelectElement>)
											}
											options={relatedTiersOptions}
											loading={loading}
											optionsLoading={loadingRelatedTiers}
										/>
										{/* Approbation */}
										<ToggleButtonField
											label="Accord Propriétaire"
											isOn={formData.relatedApproval || false}
											onChange={(value) =>
												setFormData({ ...formData, relatedApproval: value })
											}
											loading={loading}
										/>
									</CardSection>
								) : null}
							</div>
						</div>
					</form>

					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => {
							if (id) {
								navigate(`/app/quoteVersions/${id}/manage`);
							} else {
								navigate('/app/quotes');
							}
						}}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default QuoteVersionForm;
